import { useContext, useRef, useState } from 'react';

import { NavLink, useNavigate } from 'react-router-dom';

import {
  Avatar,
  Box,
  Button,
  Divider,
  Hidden,
  lighten,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography
} from '@mui/material';

import { styled } from '@mui/material/styles';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import AccountBoxTwoToneIcon from '@mui/icons-material/AccountBoxTwoTone';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import { AuthContext } from 'src/contexts/AuthContext';
import { LoadingButton } from '@mui/lab';
import { useMutation } from 'react-query';
import { fetchApi } from 'src/api/api';
import { getImageFromServer } from 'src/utils/helpers/get-image-from-server';

const UserBoxButton = styled(Button)(
  ({ theme }) => `
        padding-left: ${theme.spacing(1)};
        padding-right: ${theme.spacing(1)};
`
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${theme.colors.alpha.black[5]};
        padding: ${theme.spacing(2)};
`
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
`
);

function HeaderUserbox() {
  const { user, logout } = useContext(AuthContext);

  const { isLoading, mutateAsync } = useMutation(
    (data: any) => fetchApi('/auth/user/logout', 'POST', data),
    {
      onSuccess: () => navigate('/login')
    }
  );

  const navigate = useNavigate();

  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleLogout = () => {
    const data = {
      device_id: localStorage.getItem('deviceId')
    };
    mutateAsync(data)
      .then((res) => {
        logout();
      })
      .catch((error) => {
        logout();
        navigate('/login');
      });
  };

  return (
    <>
      <UserBoxButton color="secondary" ref={ref} onClick={handleOpen}>
        <Avatar
          variant="rounded"
          alt={user?.first_name}
          src={getImageFromServer(user?.profile_image)}
        />
        <Hidden mdDown>
          <UserBoxText>
            <UserBoxLabel variant="body1">
              {user?.first_name + ' ' + user?.last_name}
            </UserBoxLabel>
          </UserBoxText>
        </Hidden>
        <Hidden smDown>
          <ExpandMoreTwoToneIcon sx={{ ml: 1 }} />
        </Hidden>
      </UserBoxButton>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuUserBox sx={{ minWidth: 210 }} display="flex">
          <Avatar
            variant="rounded"
            alt={user?.first_name}
            src={getImageFromServer(user?.profile_image)}
          />
          <UserBoxText>
            <UserBoxLabel variant="body1">
              {user?.first_name + ' ' + user?.last_name}
            </UserBoxLabel>
          </UserBoxText>
        </MenuUserBox>
        <Divider sx={{ mb: 0 }} />
        <List sx={{ p: 1 }} component="nav">
          <ListItem button to="/profile" component={NavLink}>
            <AccountBoxTwoToneIcon fontSize="small" />
            <ListItemText primary="My Profile" />
          </ListItem>
        </List>
        <Divider />
        <Box sx={{ m: 1 }}>
          <LoadingButton
            loading={isLoading}
            onClick={handleLogout}
            color="primary"
            fullWidth
            loadingPosition="end"
            endIcon=""
          >
            <LockOpenTwoToneIcon sx={{ mr: 1 }} />
            Sign out
          </LoadingButton>
        </Box>
      </Popover>
    </>
  );
}

export default HeaderUserbox;
